import { useEffect, useState } from "react";
import { buildHead } from "@/lib/seo-head";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { UniversalLeadForm as LeadForm } from "@/components/site/UniversalLeadForm";
import { CreditSection } from "@/components/site/CreditSection";
import { SelectedRateSummary } from "@/components/rates/SelectedRateSummary";
import {
  loadSelectedRate,
  selectedRateSnapshot,
  type SelectedRateQuote,
} from "@/lib/rate-selection-store";
import {
  loadOfferAttribution,
  offerAttributionSnapshot,
  type OfferAttribution,
} from "@/lib/offer-attribution";
import type { StepperInitialValues } from "@/components/site/PreApprovalStepper";


type ApplySearch = {
  source?: string;
  product?: string;
  goal?: string;
  subject_address?: string;
  rent?: number;
  loan_amount?: number;
  estimated_value?: number;
  rate?: number;
  ltv_cap?: number;
  dscr?: number;
  pitia?: number;
  max_loan?: number;
  state?: string;
  city?: string;
  summary?: string;
};

const num = (v: unknown) => {
  const n = typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? n : undefined;
};
const str = (v: unknown) => (typeof v === "string" && v.length ? v : undefined);

export const Route = createFileRoute("/apply/")({
  validateSearch: (raw: Record<string, unknown>): ApplySearch => ({
    source: str(raw.source),
    product: str(raw.product),
    goal: str(raw.goal),
    subject_address: str(raw.subject_address),
    rent: num(raw.rent),
    loan_amount: num(raw.loan_amount),
    estimated_value: num(raw.estimated_value),
    rate: num(raw.rate),
    ltv_cap: num(raw.ltv_cap),
    dscr: num(raw.dscr),
    pitia: num(raw.pitia),
    max_loan: num(raw.max_loan),
    state: str(raw.state),
    city: str(raw.city),
    summary: str(raw.summary),
  }),
  head: () =>
    buildHead({
      title: "Apply for a DSCR or Investor Loan | Simply Approved",
      description:
        "Start your DSCR or business-purpose investor loan request in minutes. Share your scenario and a licensed loan officer reviews it and responds, usually the same business day.",
      url: "/apply",
      keywords:
        "DSCR loan application, investor loan application, business purpose loan request, apply for DSCR loan, rental property loan application, short application DSCR, investment property financing request",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Apply", url: "/apply" },
      ],
      service: { name: "DSCR & Business-Purpose Loan Application" },
      faqs: [
        {
          q: "Is this a full loan application?",
          a: "No. This is a short pre-qualification request, not a TILA/RESPA loan application. It does not ask for a Social Security number, date of birth, or personal income, and submitting it is not an offer of credit.",
        },
        {
          q: "How fast will a loan officer respond?",
          a: "A licensed loan officer reviews each scenario and typically responds the same business day with the documents needed to move forward.",
        },
        {
          q: "Do you lend on owner-occupied homes?",
          a: "No. Simply Approved Mortgages arranges business-purpose loans on non-owner-occupied investment property only.",
        },
      ],
    }),
  component: Apply,
});

function Apply() {
  const s = Route.useSearch();
  const purpose = s.goal?.toLowerCase().includes("cash") ? "Cash-out refi"
    : s.goal?.toLowerCase().includes("refi") ? "Rate/term refi"
    : "Purchase";

  // A quote selected on the rate center is handed over via sessionStorage.
  const fromRateCenter = s.source === "rate-center";
  const [quote, setQuote] = useState<SelectedRateQuote | null>(null);
  const [offer, setOffer] = useState<OfferAttribution | null>(null);
  const [ready, setReady] = useState(!fromRateCenter);
  // Offer attribution is read on every visit — a visitor can also land here
  // from a page-level rate section (source=rate-section).
  useEffect(() => {
    setOffer(loadOfferAttribution());
    if (!fromRateCenter) return;
    setQuote(loadSelectedRate());
    setReady(true);
  }, [fromRateCenter]);

  const snapshot: Record<string, string | number | boolean> = {};
  if (s.subject_address) snapshot.subject_address = s.subject_address;
  if (s.state) snapshot.state = s.state;
  if (s.city) snapshot.city = s.city;
  if (s.dscr != null) snapshot.calculated_dscr = s.dscr;
  if (s.pitia != null) snapshot.estimated_pitia = s.pitia;
  if (s.max_loan != null) snapshot.calculator_max_loan = s.max_loan;
  if (s.ltv_cap != null) snapshot.calculator_ltv_cap = s.ltv_cap;
  if (s.summary) snapshot.scenario_summary = s.summary;
  if (s.source) snapshot.calculator_source = s.source;
  if (quote) Object.assign(snapshot, selectedRateSnapshot(quote));
  Object.assign(snapshot, offerAttributionSnapshot(offer));

  const initialValues: StepperInitialValues = {
    purpose: purpose as never,
    ...(s.estimated_value != null ? { price: s.estimated_value } : {}),
    ...(s.loan_amount != null ? { currentBalance: s.loan_amount } : {}),
    ...(s.rent != null ? { expectedRent: s.rent } : {}),
    ...(s.rate != null ? { rate: s.rate } : {}),
    calculatorSnapshot: snapshot,
  };

  const banner = s.source === "dscr-toolkit" && (s.dscr != null || s.max_loan != null || s.rent != null);


  return (
    <SiteShell hideCreditSection>
      <PageHero
        eyebrow="Get pre-qualified"
        title="Start your investor scenario review."
        subtitle={
          <p>Takes about 60 seconds — just your property basics and contact info. A licensed loan officer reviews your scenario, typically the same business day. No obligation.</p>
        }
      />
      <div className="mx-auto max-w-5xl px-4 pt-4">
        <details className="rounded-lg border border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          <summary className="cursor-pointer font-semibold text-foreground">Important disclosures</summary>
          <p className="mt-2 leading-relaxed">No obligation. No guaranteed approval. This is a request for pre-qualification for business-purpose investment property financing only and is not a commitment to lend or an offer of credit. Any rates, payments, loan amounts, terms, or other figures shown are estimates only and may change. Final eligibility, rates, terms, and approval are subject to a completed application, credit and property review, appraisal where required, verification of information, lender requirements, and full underwriting.</p>
        </details>
      </div>
      <Section>
        <div className="mx-auto max-w-5xl space-y-6">
          {banner && (
            <div className="rounded-xl border border-brand/30 bg-brand/5 p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-brand">
                Your calculator scenario was saved
              </div>
              <p className="mt-1 text-sm text-foreground">
                We've pre-filled this application with the numbers from your DSCR toolkit run
                {s.subject_address ? <> for <span className="font-semibold">{s.subject_address}</span></> : null}.
                Adjust anything below before you submit.
              </p>
              <dl className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
                {s.dscr != null && <div><dt className="text-muted-foreground text-xs">Calculated DSCR</dt><dd className="font-semibold">{s.dscr.toFixed(2)}</dd></div>}
                {s.rent != null && <div><dt className="text-muted-foreground text-xs">Monthly rent</dt><dd className="font-semibold">${s.rent.toLocaleString()}</dd></div>}
                {s.estimated_value != null && <div><dt className="text-muted-foreground text-xs">Property value</dt><dd className="font-semibold">${s.estimated_value.toLocaleString()}</dd></div>}
                {s.max_loan != null && <div><dt className="text-muted-foreground text-xs">Max qualifying loan</dt><dd className="font-semibold">${Math.round(s.max_loan).toLocaleString()}</dd></div>}
              </dl>
            </div>
          )}
          {quote && <SelectedRateSummary quote={quote} />}
          {ready ? (
            <LeadForm
              defaultProduct={s.product ?? "dscr"}
              defaultState={s.state}
              initialValues={initialValues}
              offerMeta={offer}
              sourcePage={s.source ? `/apply?source=${s.source}` : "/apply"}
            />
          ) : (
            <div className="h-64 animate-pulse rounded-xl border border-border bg-muted/40" aria-hidden="true" />
          )}

        </div>
      </Section>
      <CreditSection />
    </SiteShell>
  );
}
